import merge from "lodash/merge";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { convertDotSeparatedStringToJSONObject, convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import { DEFAULT_EMAIL_MESSAGE_TEMPLATE, MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME, messageTemplateContentStringRegex, REQUIRED_MESSAGE_TEMPLATE_VARIABLES } from "@constants/message-template-constants";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { IDWSession } from "@idw-types/data/idw-session";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { isAxnAdminUser } from "@utils/permissions-util";
import { MessageTemplateTypes } from "@enums/message-template-types";

export const getInitialMessageTemplate = (
    axnCompanies: Array<AxnCompany>, isVericationServiceDetailsPage: boolean,
    session: IDWSession,
): FormattedMessageTemplate => {


    const initialMessageTemplate: FormattedMessageTemplate = {
        template: DEFAULT_EMAIL_MESSAGE_TEMPLATE,
        name: "",
        company: null,
        id: null,
        uuid: null,
        rpServiceID: null,
        sender: null,
        status: EntityStatusTypes.ACTIVE,
        type: null,
        languages: { en: {} },
        templateAvailabilityLevel: null,
        permissions: {
            canCreate: true,
            canModify: true,
            canView: true
        },
    };

    let company: AxnCompany = null;
    const templateAvailabilityLevel = getDefaultTemplateAvailabilityLevel(session, isVericationServiceDetailsPage);

    if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.ORGANIZATION) {
        company = axnCompanies[0];
    }

    return {
        ...initialMessageTemplate,
        templateAvailabilityLevel,
        company,
    }
}
const getDefaultTemplateAvailabilityLevel = (session: IDWSession, isVericationServiceDetailsPage: boolean) => {
    const isAdminUser = isAxnAdminUser(session.user);
    if (isAdminUser) {
        return isVericationServiceDetailsPage ? TemplateAvailabilityLevelTypes.ORGANIZATION : TemplateAvailabilityLevelTypes.GLOBAL;
    } else {
        return TemplateAvailabilityLevelTypes.ORGANIZATION;
    }
};

export const getVariablesFromMessageTemplate = (template: string): { name: string; value: string; }[] => {
    const matches = template.match(messageTemplateContentStringRegex);
    const variables: Array<{ name: string; value: string; }> = [];
    if (matches) {
        matches.forEach(match => {
            const splitVar = match.split(/!(.*)/s);
            if (splitVar && splitVar.length > 1) {
                const variableName = splitVar[0].substring(3, splitVar[0].length - 1);
                const variableValue = splitVar[1].substring(1, splitVar[1].length - 2);

                variables.push({
                    name: variableName,
                    value: variableValue
                });
            }
        });
    }

    return variables;
};

export const getMessageTemplateVariables = (languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>>, template: string, selectedLanguage: LocaleTypes) => {
    const variables = getVariablesFromMessageTemplate(template);
    let flattenedLanguageFile: { [key: string]: string } = {};

    if (languageFiles[selectedLanguage]) {
        flattenedLanguageFile = convertJSONObjectToDotSeparatedString(languageFiles[selectedLanguage]);
    }

    const finalVariables: Array<{ name: string; value: string; }> = [];
    variables.forEach(variable => {
        const { name, value } = variable;
        const isPropertyInLanguageFile = Object.keys(flattenedLanguageFile).includes(name);
        let updatedValue;
        if (!REQUIRED_MESSAGE_TEMPLATE_VARIABLES.includes(name)) {
            if (!isPropertyInLanguageFile && value) {// the variable doesn't exist in language files and we have a default value
                updatedValue = value;
            } else if (isPropertyInLanguageFile) {// the variable already exist in language files empty or not 
                updatedValue = flattenedLanguageFile[name];
            } else {
                updatedValue = value || "";
            }

            finalVariables.push({ name, value: updatedValue });
        }
    });

    return finalVariables;
}

export const removeUnusedVariablesFromMessageTemplateLanguageFiles = (
    template: string,
    languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any; }>>,
    type: string
) => {
    const variables = getVariablesFromMessageTemplate(template);
    const localeToVariablesMap: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = {};

    if (type === MessageTemplateTypes.EMAIL) {
        variables.push({ name: MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME, value: DEFAULT_EMAIL_MESSAGE_TEMPLATE });
    }
    //remove any variables that aren't in template (excluding required ones)
    (Object.keys(languageFiles) as Array<LocaleTypes>).forEach(locale => {
        const flattenedLanguageFile = convertJSONObjectToDotSeparatedString(languageFiles[locale]);
        let finalLanguageFile: { [key: string]: any } = {};
        variables.forEach(variable => {
            if (flattenedLanguageFile[variable.name]) {
                const variableJSON = convertDotSeparatedStringToJSONObject(variable.name, flattenedLanguageFile[variable.name]);
                finalLanguageFile = merge({}, finalLanguageFile, variableJSON);
            } else {
                const variableJSON = convertDotSeparatedStringToJSONObject(variable.name, variable.value);
                finalLanguageFile = merge({}, finalLanguageFile, variableJSON);
            }
        });
        localeToVariablesMap[locale] = finalLanguageFile;
    });
    return localeToVariablesMap;
};

export const getUnformattedLanguageFiles = (languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>>): Partial<MultiLanguageWrapper<string>> => {
    return (Object.keys(languageFiles || {}) as Array<LocaleTypes>).reduce((result, locale) => {
        const localeString = JSON.stringify(languageFiles[locale]) || "";
        result[locale] = localeString;
        return result;

    }, {} as Partial<MultiLanguageWrapper<string>>);
};